import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import Captcha from "./Captcha";

export default function AdminLogin({ studio = false }) {
  const { user, setUser, authChecked } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ identity: "", password: "", remember: false });
  const [captcha, setCaptcha] = useState({ captcha_id: "", answer: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [capKey, setCapKey] = useState(0);

  useEffect(() => {
    if (authChecked && user) {
      navigate(studio ? "/" : user.role === "greeting" ? "/admin/greetings" : "/admin", { replace: true });
    }
  }, [user, authChecked]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/login", {
        identity: form.identity, password: form.password, remember: form.remember,
        captcha_id: captcha.captcha_id, captcha_answer: captcha.answer,
      });
      setUser(r.data);
      toast.success(`Welcome, ${r.data.name || r.data.email}`);
      navigate(studio ? "/" : r.data.role === "greeting" ? "/admin/greetings" : "/admin");
    } catch (err) {
      setError(errMsg(err));
      setCapKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-navy px-4 py-10" data-testid="superadmin-login-page">
      <SEO title="Superadmin Login" />
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-2xl sm:p-10">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.png" alt="Sahaj Sarathi Foundation logo" className="h-16 w-16 rounded-full bg-white object-contain ring-2 ring-saffron-500/50" />
          <h1 className="mt-4 font-display text-xl font-bold tracking-tight text-navy">SAHAJ SARATHI FOUNDATION</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-saffron-600">{studio ? "AI Greeting Studio · Staff Login" : "Superadmin Login"}</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="superadmin-login-form">
          <div>
            <label htmlFor="login-identity" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Username or Email</label>
            <input id="login-identity" data-testid="login-identity" required value={form.identity}
              onChange={(e) => setForm({ ...form, identity: e.target.value })} autoComplete="username"
              className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
          </div>
          <div>
            <label htmlFor="login-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
            <div className="relative">
              <input id="login-password" data-testid="login-password" type={showPw ? "text" : "password"} required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password"
                className="w-full rounded-md border border-gray-200 px-4 py-2.5 pr-11 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Show or hide password" data-testid="login-show-password"
                className="absolute right-3 top-2.5 text-slate-400 hover:text-navy">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Captcha key={capKey} onChange={setCaptcha} />
          <label className="flex items-center gap-2.5 text-sm font-semibold text-navy">
            <input type="checkbox" checked={form.remember} onChange={(e) => setForm({ ...form, remember: e.target.checked })}
              className="h-4 w-4 accent-saffron-500" data-testid="login-remember" />
            Remember Me
          </label>
          {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} Sign In
          </button>
        </form>
        {!studio && (
          <div className="mt-6 flex items-center justify-between text-xs font-semibold">
            <Link to="/superadmin/forgot-password" className="text-saffron-600 hover:text-navy" data-testid="forgot-password-link">Forgot Password?</Link>
            <Link to="/superadmin/signup" className="text-slate-500 hover:text-navy" data-testid="create-account-link">Create Account</Link>
          </div>
        )}
        {studio
          ? <a href="https://www.sahajsarathi.com" className="mt-4 block text-center text-xs font-semibold text-slate-400 hover:text-saffron-600" data-testid="back-to-site">← Back to website</a>
          : <Link to="/" className="mt-4 block text-center text-xs font-semibold text-slate-400 hover:text-saffron-600" data-testid="back-to-site">← Back to website</Link>}
      </div>
    </div>
  );
}
